import {
  COLLISION_WALL_2D,
  COLLISION_WALL_RANGE_THICKNESS,
  MODE_DRAGGING_HOLE,
  MODE_DRAGGING_WALL,
  MODE_IDLE,
} from '@constants'
import { Base, Geometry, Subtraction } from '@react-three/csg'
import { Edges, Text } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import { useAppStore } from '@store'
import { Hole, Store, Walls } from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import {
  calculateRelativePositionNext,
  calculateRelativePositionPrev,
} from '@utils/generalMaths/calcRelativePosition'
import {
  calcRelativeRotation,
  calcRelativeRotationprevWall,
} from '@utils/generalMaths/calcRelativeRotation'
import { degrees } from '@utils/generalMaths/geometry'
import isHole from '@utils/sceneObjects/assertions/isHole'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Euler, Matrix3, Matrix4 } from 'three'
import { ninetyDeg, oneEightyDeg } from '../../../store/baseValues'
import VertexHandler from './../Vertex/VertexHandler'
import AngleHandler from './AngleHandler'
import DimentionsHandler from './DimentionsHandler'
import React from 'react'

type Props = {
  id: string
  width: number
  height: number
  depth: number
  rotation: number
  holes: Set<string>
  thickness: number
  color: string
  start: [number, number]
  y: number
  visible: boolean
}

export function Wall2D({ visible, id, width, height, thickness, start, y }: Props) {
  const meshRef = useRef(null)

  const item = useAppStore.use.items().get(id) as Walls

  const nextWall = useAppStore((s) => s.items.get(id).nextWall)
  const rotation = item.rotation
  const prevWall = useAppStore((s) => s.items.get(id).prevWall)
  const PREF_WALL = useAppStore.use.prefs()?.['C/LISOPARED2D']
  const x = item.x
  const z = item.z
  const [sx, sz] = item.start
  const [ex, ez] = item.end
  const navigate = useNavigate()

  const holesVersion = useAppStore((s) => {
    const wall = s.items.get(id)
    if (!wall || wall.type !== 'walls') return 0
    let acc = 0
    wall.holes?.forEach((holeId: string) => {
      const hole = s.items.get(holeId as any)
      if (hole && hole.type === 'holes') {
        acc += (hole as any).width || 0
        acc += (hole as any).distI || 0
        acc += (hole as any).distD || 0
      }
    })
    return acc
  })
  const isSelected = useAppStore((store: Store) =>
    store.layers[store.currentLayer].selected.has(id),
  )
  const sceneBoundingBoxes = useAppStore((s) => s.sceneBoundingBoxes)
  const wallCollisionInfo = sceneBoundingBoxes.get(getWallCOBB(id))
  const mode = useAppStore.use.mode()
  const select = useAppStore.use.select()
  const setMode = useAppStore.use.setMode()
  const setCameraMove = useAppStore.use.setCameraMove()
  const holes = Array.from(item.holes)
  const currentObjectID = useAppStore((store: Store) => store.currentObjectID)
  const currObj = useAppStore((store: Store) => store.items.get(store.currentObjectID))
  const closeModal = useAppStore.use.closeModal()
  const innerAngle = useAppStore((store: Store) => store.items.get(id)?.innerAngle)

  const isModalOpen = useAppStore.use.isModalOpen()
  const openModal = useAppStore.use.openModal()
  const items = useAppStore((store) => store.items)
  const nextItem = useAppStore.use.items().get(nextWall) as Walls
  const prevItem = useAppStore.use.items().get(prevWall)
  const wallVisible = useAppStore.use.items().get(id)?.wallVisible
  const nlr = useAppStore((s) => s.items.get(nextWall)?.rotation),
    plr = useAppStore((s) => s.items.get(prevWall)?.rotation),
    plb = useAppStore((store) => store.items.get(prevWall)?.beta),
    cross = useAppStore((store) => store.items.get(id)?.cross),
    dot = useAppStore((store) => store.items.get(id)?.dot),
    nlia = useAppStore((s) => s.items.get(nextWall)?.innerAngle),
    absoluteCross = useAppStore((s) => s.items.get(id)?.absoluteCross),
    plc = useAppStore((s) => s.items.get(prevWall)?.cross),
    pld = useAppStore((s) => s.items.get(prevWall)?.dot),
    plac = useAppStore((s) => s.items.get(prevWall)?.absoluteCross),
    beta = useAppStore((store) => store.items.get(id)?.beta),
    nlx = useAppStore((store) => store.items.get(nextWall)?.x),
    nlt = useAppStore((store) => store.items.get(nextWall)?.thickness),
    setFrameloop = useAppStore.use.setFrameloop()

  const WALL_WIDTH = 3000

  const perpendicularPoint = useAppStore((store) => store.items.get(id)?.perpendicularPoint)

  const symmetricalPerpendicularPoint = useAppStore(
    (store) => store.items.get(prevWall)?.symmetricalPerpendicularPoint,
  )

  const perpendicularPointArray = perpendicularPoint
    ? [(perpendicularPoint.x, perpendicularPoint.z, perpendicularPoint.y)]
    : [0, 0, 0]
  const symmetricalPerpendicularPointArray = symmetricalPerpendicularPoint
    ? [
        symmetricalPerpendicularPoint.x,
        symmetricalPerpendicularPoint.z,
        symmetricalPerpendicularPoint.y,
      ]
    : [0, 0, 0]

  function handleClick(e: ThreeEvent<MouseEvent>) {
    if (mode === 'MODE_DRAWING_ITEM' || mode === 'MODE_DRAGGING_ITEM' || isModalOpen) return
    if (!visible) return
    if (mode === MODE_IDLE) {
      if (!isSelected) {
        select(id, false)
        e.stopPropagation()
      }
    }
    if (mode === MODE_DRAGGING_WALL) {
      e.stopPropagation()
      setMode('MODE_IDLE')
    }
    if (mode === MODE_DRAGGING_HOLE) {
      e.stopPropagation()
      setColor(PREF_WALL)
      setFrameloop('demand')
      openModal()
      if (currObj?.tipoUnidNorm === 'V') {
        navigate(`/planner/window/${currObj?.idUnidades}.${id}`)
        return
      }
      if (currObj?.tipoUnidNorm === 'P') {
        navigate(`/planner/door/${currObj?.idUnidades}.${id}`)
        return
      }
      closeModal()
    }
  }

  function handleDragStart() {
    if (isModalOpen) return
    switch (mode) {
      case 'MODE_IDLE':
        if (!isSelected) return
        setMode('MODE_DRAGGING_WALL')
        setCameraMove(false)
        break
      default:
        break
    }
  }

  function handleDragEnd() {
    if (isModalOpen) return
    switch (mode) {
      case 'MODE_DRAGGING_WALL':
        if (!isSelected) return
        setMode('MODE_IDLE')
        setCameraMove(true)
        break
      default:
        break
    }
  }

  useEffect(() => {
    setColor(PREF_WALL)
  }, [currentObjectID])

  const middleAngle = rotation + Math.abs(innerAngle) / 2
  let xT = 0,
    zT = 0
  const PREFS_COLOR_TEXTO = useAppStore((store) => store.prefs?.['C/COTA'])

  const FONT_SIZE = useAppStore.use.prefs()?.['T/LETRACOTA'] ?? 100
  const showAngle = item.showAngle
  const dist = 350
  xT = dist * Math.cos(middleAngle)
  zT = dist * Math.sin(middleAngle)

  const VER90 = useAppStore.use.prefs()?.VER90
  const VERANGULOS = useAppStore.use.prefs()?.VERANGULOS
  const [wallColor, setColor] = useState<string>(PREF_WALL)
  useEffect(() => {
    if (wallColor !== PREF_WALL) setColor(PREF_WALL)
  }, [PREF_WALL])
  const COLOR4 = useAppStore.use.prefs()?.['C/Color4']

  // Memoizar solo la geometría
  const geometryArgs = useMemo(() => [width + 1000, 1, thickness], [width, thickness])

  //  Separar
  useLayoutEffect(() => {
    if (!wallCollisionInfo || !meshRef?.current) return

    const cobb = wallCollisionInfo.boundingBox
    if (!cobb) return
    cobb.center.set(x, y, z)
    cobb.halfSize.set(
      (width + COLLISION_WALL_RANGE_THICKNESS) / 2,
      COLLISION_WALL_2D,
      (thickness + COLLISION_WALL_RANGE_THICKNESS) / 2,
    )

    // const obb = wallBBInfo.boundingBox
    // obb.center.set(x, y, z)
    // obb.halfSize.set(width / 2, height, thickness / 2)

    const mat4 = new Matrix4().makeRotationFromEuler(
      new Euler(0, -rotation + 2 * Math.PI, 0, 'XYZ'),
    )
    const rot = new Matrix3().setFromMatrix4(mat4)
    cobb.rotation = rot
    // obb.rotation = rot
  }, [x, y, z, width, height, thickness, rotation, wallCollisionInfo])

  const shouldBeSeen =
    VERANGULOS &&
    innerAngle &&
    showAngle &&
    (innerAngle % ninetyDeg === 0 ? VER90 : true && innerAngle)
  // const shouldBeSeen =
  //   VERANGULOS &&
  //   showAngle &&
  //   innerAngle &&
  //   (innerAngle % ninetyDeg === 0 || innerAngle % ninetyDeg !== 0)
  const pwv = useAppStore((s) => s.items.get(prevWall)?.visible)
  return (
    <group
      visible={visible && wallVisible && true}
      key={`wall-group-${id}-${beta}`}
    >
      {/* <group position={[100, 0, 0]}>
        <mesh>
          <group>
            <mesh>
              <boxGeometry args={[100, 100, 100]} />
              <meshBasicMaterial color='red' />
            </mesh>
            <mesh position={[200, 0, 0]}>
              <boxGeometry args={[100, 100, 100]} />
              <meshBasicMaterial color='red' />
            </mesh>
          </group>
        </mesh>
      </group> */}
      {shouldBeSeen && (
        <Text
          key={`wall2D-${id}-angle-${innerAngle}-text-${start[0]}-${start[1]}}`}
          position={[start[0] + xT, 3, start[1] + zT]}
          fontSize={FONT_SIZE}
          characters='abcdefghijklmnopqrstuvwxyz0123456789!º'
          color={PREFS_COLOR_TEXTO}
          rotation={[-ninetyDeg, 0, -ninetyDeg]}
        >
          {String(degrees(innerAngle).toFixed(2)) + 'º'}
        </Text>
      )}
      <mesh
        ref={meshRef}
        position={[x, 1, z]}
        rotation={[0, -rotation, 0]}
        onClick={handleClick}
        onPointerDown={handleDragStart}
        onPointerUp={handleDragEnd}
        onPointerEnter={() => mode === 'MODE_DRAGGING_HOLE' && setColor(COLOR4)}
        onPointerLeave={() => mode === 'MODE_DRAGGING_HOLE' && setColor(PREF_WALL)}
        onPointerMissed={() => (wallColor !== PREF_WALL ? setColor(PREF_WALL) : '')}
        key={`wall2D-${beta}-${nlx}-${x}-${z}-${symmetricalPerpendicularPointArray.join(
          '-',
        )}-${perpendicularPointArray.join(
          '-',
        )}-${width}-${id}-start:${sx}:${sz}-end:[${ex},${ez}]-${prevWall}-${nextWall}-${prevItem?.nextWall}-${nextItem?.prevWall}`}
      >
        {shouldBeSeen && pwv && <AngleHandler id={id} />}
        <DimentionsHandler id={id} />
        {mode !== 'MODE_DRAGGING_HOLE' && (
          <group visible={isSelected}>
            <VertexHandler
              id={id}
              position={[x, y, z]}
              width={width}
              thickness={thickness}
              isSelected={isSelected}
            />
          </group>
        )}
        <Geometry showOperations={false}>
          <Base
            key={`${x}-${y}-${z}`}
            position={[0, 0, 0]}
          >
            <boxGeometry args={geometryArgs} />
          </Base>
          {!!nextWall && nlia !== oneEightyDeg && nlx !== 200000 && nlt !== 0.1 ? (
            <Subtraction
              showOperation={false}
              rotation={[
                0,
                calcRelativeRotation({
                  rotationA: rotation,
                  beta,
                  rotationB: nlr,
                  absoluteCross,
                  cross,
                  dot,
                }),
                0,
              ]}
              position={calculateRelativePositionNext(x, y, z, perpendicularPoint, rotation)}
            >
              <boxGeometry args={[WALL_WIDTH, height, 600]} />
              <meshBasicMaterial color={'green'} />
            </Subtraction>
          ) : (
            <Subtraction
              showOperation={false}
              position={[width / 2 + 500, 0, 0]}
              rotation={[0, ninetyDeg, 0]}
            >
              <boxGeometry args={[1000, 1, 1000]} />
              <meshBasicMaterial color={'green'} />
            </Subtraction>
          )}
          {!!prevWall && innerAngle !== oneEightyDeg && plb && plb !== 0 ? (
            <Subtraction
              showOperation={false}
              rotation={[
                0,
                calcRelativeRotationprevWall({
                  absoluteCross: plac,
                  beta: plb,
                  cross: plc,
                  rotationA: plr,
                  rotationB: rotation,
                  dot: pld,
                }),
                0,
              ]}
              position={calculateRelativePositionPrev(
                x,
                y,
                z,
                symmetricalPerpendicularPoint,
                rotation,
              )}
            >
              <boxGeometry args={[WALL_WIDTH, height, 600]} />
              <meshBasicMaterial color={'peru'} />
            </Subtraction>
          ) : (
            <Subtraction
              showOperation={false}
              rotation={[0, ninetyDeg, 0]}
              position={[-width / 2 - 500, 0, 0]}
            >
              <boxGeometry args={[1000, 1, 1000]} />
              <meshBasicMaterial color={'peru'} />
            </Subtraction>
          )}

          {holes &&
            holes.length >= 1 &&
            holes?.map((el) => {
              const elemento = items.get(el)
              if (!elemento) return null
              const { matrix, distI, morphs } = elemento satisfies Hole
              const positionVector = toVector3()
              matrix.decompose(positionVector, toQuaternion(), toVector3())
              const ancho = morphs.find((el: { name: string }) => el.name === 'Ancho') ?? {
                _default: null,
                default: null,
              }
              const fondo = morphs.find((el: { name: string }) => el.name === 'Fondo') ?? {
                _default: null,
                default: null,
              }
              const anchoValue = ancho._default ?? ancho.default ?? 0
              return (
                <Subtraction
                  key={`holeSubstraction-${id}-${distI}-${anchoValue}-${fondo?._default ?? fondo?.default ?? 0}-${elemento?.id}`}
                  position={[distI - width / 2 + anchoValue / 2, -100, 0]}
                  rotation={[0, 0, 0]}
                >
                  <boxGeometry args={[anchoValue, 300, thickness + 50]} />
                </Subtraction>
              )
            })}
        </Geometry>
        <meshBasicMaterial
          toneMapped={false}
          color={wallColor}
        />
        <Edges
          visible
          key={`Edges-Wall2D-${x}-${y}-${z}-$${rotation}-${width}-${thickness}-${id}-${
            holes.length
          }-${holes
            .map((value) => {
              const hole = items.get(value)
              if (!isHole(hole)) return null
              return `${hole.distI}-${hole.width}-${hole.id}-${hole?.thickness}`
            })
            .concat('-')}-${holesVersion}`}
          // geometry-attributes-needsUpdate={true}
        >
          <meshBasicMaterial
            depthTest={true}
            toneMapped={false}
            color='black'
          />
        </Edges>
      </mesh>
    </group>
  )
}

export default React.memo(Wall2D)
